import { usePackageBookingStore } from "@elixir/stores";
import {
  PACKAGE_POPUP_TYPE,
  PACKAGE_PRICING_MODEL,
  PACKAGE_TYPE,
} from "@elixir/types";
import {
  formatSessionsToInclusions,
  formattedPackageDetails,
} from "@elixir/utils";
import { Drawer, notification } from "antd";
import React from "react";
import { ModalFooter, DrawerHeaderUi } from "ui";
import usePostPackageConfig from "../../../../apis/tryst/packages-and-treatments/use-post-package-config";
import { track } from "../../../../utils/tracking";
import PackageBreakdown from "../popup-content/package-breakdown";
import SessionDetails from "../popup-content/session-details";

const CreatePackagePopup = () => {
  React.useEffect(() => {
    track("create_package_popup");
  }, []);

  const {
    mutateAsync: postPackageConfig,
    isLoading: postPackageConfigLoading,
  } = usePostPackageConfig();

  const packageDetails = usePackageBookingStore(
    (state) => state.packageDetails
  );
  const clearPackageDetails = usePackageBookingStore(
    (state) => state.clearPackageDetails
  );
  const packagePopupType = usePackageBookingStore(
    (state) => state.packagePopupType
  );
  const setPackagePopupType = usePackageBookingStore(
    (state) => state.setPackagePopupType
  );

  const sessionList = usePackageBookingStore((state) => state.sessionList);
  const setSessionList = usePackageBookingStore(
    (state) => state.setSessionList
  );

  const handleClosePopup = () => {
    clearPackageDetails();
  };

  React.useEffect(() => {
    if (packageDetails?.inclusions) {
      setSessionList(formattedPackageDetails(packageDetails?.inclusions));
    } else {
      setSessionList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageDetails?.inclusions]);

  const isPricingModelNotPresent = !packageDetails?.pricing_model;
  const isSessionListEmpty = sessionList.length === 0;
  const isPricingModelOneTime =
    PACKAGE_PRICING_MODEL.ONE_TIME === packageDetails?.pricing_model;
  const isTotalPriceNotPresent = isPricingModelOneTime
    ? !packageDetails?.total_price
    : false;
  const isPackageNameNotPresent = !packageDetails?.package_name;
  const isPackageSessionNameNotPresent = sessionList.some(
    (session) => !session.name
  );
  const isPackageTypeNotPresent = !packageDetails?.type;
  const isPackageTypeSubscriptionPlan =
    packageDetails?.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN;
  const isPackageExpiryNotPresent =
    isPackageTypeSubscriptionPlan &&
    !packageDetails?.package_expiry_from_start &&
    packageDetails?.package_expiry_from_start !== 0;
  const isPackageFamilyMembersNotPresent =
    isPackageTypeSubscriptionPlan &&
    (packageDetails?.max_members < 1 || !packageDetails?.max_members);

  const isDisabled =
    isSessionListEmpty ||
    isPackageNameNotPresent ||
    isTotalPriceNotPresent ||
    isPricingModelNotPresent ||
    isPackageSessionNameNotPresent ||
    isPackageTypeNotPresent ||
    isPackageExpiryNotPresent ||
    isPackageFamilyMembersNotPresent;

  const handleOkClick = async () => {
    if (!packageDetails) return;
    const response = await postPackageConfig({
      data: {
        ...packageDetails,
        inclusions: formatSessionsToInclusions(sessionList),
        number_of_inclusions: sessionList?.length,
        total_price:
          packageDetails?.pricing_model === PACKAGE_PRICING_MODEL.ONE_TIME
            ? packageDetails?.total_price
            : undefined,
        max_members:
          packageDetails?.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN
            ? packageDetails?.max_members
            : 1,
        package_expiry_from_start:
          packageDetails?.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN
            ? packageDetails?.package_expiry_from_start
            : undefined,
      },
    });
    if (response?.success) {
      notification.success({
        message: "Package created successfully",
        placement: "bottomRight",
      });
      setPackagePopupType(PACKAGE_POPUP_TYPE.NONE);
    } else {
      notification.error({
        message: "Package creation failed",
        placement: "bottomRight",
      });
    }
  };

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      closable={false}
      placement={"right"}
      onClose={handleClosePopup}
      open={packagePopupType === PACKAGE_POPUP_TYPE.CREATE_PACKAGE}
      width={"90%"}
      extra={null}
      destroyOnClose={true}
      footer={
        <ModalFooter
          isOkButtonDisabled={isDisabled}
          onCancel={handleClosePopup}
          onOk={handleOkClick}
          isSubmitting={postPackageConfigLoading}
          okText={
            packageDetails?.type === PACKAGE_TYPE.TREATMENT_PLAN
              ? "Create Treatment Package"
              : "Create Subscription Package"
          }
        />
      }
    >
      <DrawerHeaderUi
        title={
          packageDetails?.type === PACKAGE_TYPE.TREATMENT_PLAN
            ? "Create Treatment Package"
            : "Create Subscription Package"
        }
        onClose={handleClosePopup}
      />
      <div className="dr-bg-darwin-neutral-50 dr-p-16 dr-h-full dr-flex dr-flex-col dr-gap-y-24">
        <PackageBreakdown />
        <SessionDetails pricingModel={packageDetails?.pricing_model} />
      </div>
    </Drawer>
  );
};

export default CreatePackagePopup;
